import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { SUPPORTED_LANGS } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

/**
 * Language picker shared by the sidebar and the Settings page.
 *
 * Behaviour:
 * 1. Switch i18next immediately so the UI re-renders without a round-trip.
 * 2. Persist to localStorage (handled by i18next-browser-languagedetector).
 * 3. Best-effort sync to the backend so emails / PDFs honour the same
 *    preference. Failures are swallowed — the UX is fine without the
 *    server bit working.
 */
export default function LanguageSwitcher({ variant = "compact", testid = "lang-switcher" }) {
  const { i18n, t } = useTranslation();
  const { user, setUser } = useAuth();
  const current = SUPPORTED_LANGS.find((l) => l.code === i18n.resolvedLanguage)
    || SUPPORTED_LANGS.find((l) => l.code === i18n.language?.split("-")[0])
    || SUPPORTED_LANGS[0];

  const pick = async (code) => {
    if (code === i18n.language) return;
    await i18n.changeLanguage(code);
    // Optimistically update local user state so other tabs/components
    // reading `user.preferred_language` re-render too.
    if (user && setUser) {
      setUser({ ...user, preferred_language: code });
    }
    try {
      await api.patch("/auth/me/language", { language: code });
    } catch {
      // Non-fatal; the localStorage cache still keeps the choice across reloads.
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={
            variant === "compact"
              ? "w-full flex items-center gap-2 px-2 py-2 text-sm text-zinc-600 hover:text-ink hover:bg-white rounded-md transition-colors"
              : "inline-flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm hover:border-zinc-400 transition-colors"
          }
          title={t("common.switch_language")}
        >
          <Globe className="h-4 w-4" />
          <span className="text-base leading-none" aria-hidden>{current.flag}</span>
          <span className="text-xs truncate flex-1 text-left">{current.label}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>{t("common.switch_language")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SUPPORTED_LANGS.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onClick={() => pick(l.code)}
            data-testid={`lang-option-${l.code}`}
            className={l.code === current.code ? "bg-zinc-100 font-medium" : ""}
          >
            <span className="text-base leading-none mr-2" aria-hidden>{l.flag}</span>
            <span className="text-sm">{l.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
